/**
 * 浏览器系统桌面通知（Web Notification API）与消息提示音工具封装
 */

export interface DesktopNotificationOptions {
  title?: string;
  summary?: string;
  body?: string;
  related?: {
    orderId?: string | number;
    [key: string]: any;
  };
  onClick?: () => void;
}

export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!isNotificationSupported()) {
    return Promise.resolve('unsupported');
  }
  if (Notification.permission === 'granted') {
    return Promise.resolve('granted');
  }
  if (Notification.permission !== 'denied') {
    return Notification.requestPermission();
  }
  return Promise.resolve(Notification.permission);
}

export function showDesktopNotification(options: DesktopNotificationOptions): Notification | null {
  const { title, summary, body, related, onClick } = options;
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return null;
  }

  try {
    const notificationOptions: NotificationOptions & { renotify?: boolean; requireInteraction?: boolean } = {
      body: body || summary || '',
      icon: '/img/logo.svg',
      tag: related?.orderId ? `order_${related.orderId}_${Date.now()}` : `survey_msg_${Date.now()}`,
      renotify: true,
      requireInteraction: true // 保持悬浮，直至用户点击或手动关闭
    };
    const notification = new Notification(title || '问卷助手通知', notificationOptions);

    notification.onclick = function () {
      window.focus();
      if (typeof onClick === 'function') {
        onClick();
      }
      notification.close();
    };

    return notification;
  } catch (e) {
    console.error('Failed to show desktop notification:', e);
    return null;
  }
}

let audioCtx: AudioContext | null = null;

/**
 * 播放新消息提示音（叮咚~ 880Hz -> 1318.5Hz 双音和弦，使用 Web Audio API，无需额外音频资源）
 */
export function playNotificationSound(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    if (!audioCtx) {
      audioCtx = new AudioContextClass();
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const now = audioCtx.currentTime;

    // 音调 1: 880Hz (A5)
    const osc1 = audioCtx.createOscillator();
    const gain1 = audioCtx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    gain1.gain.setValueAtTime(0.25, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    osc1.connect(gain1);
    gain1.connect(audioCtx.destination);
    osc1.start(now);
    osc1.stop(now + 0.22);

    // 音调 2: 1318.5Hz (E6) 稍后切入，构成轻快的叮咚声
    const osc2 = audioCtx.createOscillator();
    const gain2 = audioCtx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1318.5, now + 0.1);
    gain2.gain.setValueAtTime(0.28, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.42);
    osc2.connect(gain2);
    gain2.connect(audioCtx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.42);
  } catch (e) {
    console.warn('Failed to play notification sound:', e);
  }
}

export {
  formatUnreadCount,
  getBaseTitle,
  setUnreadTitleCount,
  notifyNewMessageTitle,
  resetPageTitle,
  setOriginalTitle
} from './titleNotifier';
